function resolveApiBase(): string {
  const envBase = import.meta.env.VITE_API_BASE_URL;
  if (envBase && envBase.length > 0) {
    const cleanedBase = envBase.endsWith('/') ? envBase.slice(0, -1) : envBase;

    // Guard against accidentally deploying a localhost API base URL to production.
    if (typeof window !== 'undefined') {
      const appHost = window.location.hostname;
      const appIsLocal =
        appHost === 'localhost' || appHost === '127.0.0.1' || appHost.endsWith('.local');

      try {
        const parsed = new URL(cleanedBase);
        const apiHost = parsed.hostname;
        const apiIsLocal =
          apiHost === 'localhost' || apiHost === '127.0.0.1' || apiHost.endsWith('.local');

        if (!appIsLocal && apiIsLocal) {
          return '/api';
        }
      } catch {
        // Relative values such as /api are valid and should pass through.
      }
    }

    return cleanedBase;
  }

  return '/api';
}

function normalizePath(path: string): string {
  return path.startsWith('/') ? path : `/${path}`;
}

function isAbsoluteHttpUrl(value: string): boolean {
  return value.startsWith('http://') || value.startsWith('https://');
}

export function buildApiUrl(apiBase: string, path: string): string {
  const normalizedBase = apiBase.endsWith('/') ? apiBase.slice(0, -1) : apiBase;
  const normalizedPath = normalizePath(path);

  if (!isAbsoluteHttpUrl(normalizedBase)) {
    return `${normalizedBase}${normalizedPath}`;
  }

  const baseUrl = new URL(normalizedBase);
  const basePath = baseUrl.pathname.endsWith('/')
    ? baseUrl.pathname.slice(0, -1)
    : baseUrl.pathname;
  baseUrl.pathname = `${basePath}${normalizedPath}`;
  baseUrl.search = '';
  baseUrl.hash = '';
  return baseUrl.toString();
}

export function buildApiWebSocketUrl(
  apiBase: string,
  path: string,
  currentOrigin: string,
  currentProtocol: string,
): string {
  const normalizedBase = apiBase.endsWith('/') ? apiBase.slice(0, -1) : apiBase;
  const normalizedPath = normalizePath(path);
  const socketProtocol = currentProtocol === 'https:' ? 'wss:' : 'ws:';

  if (isAbsoluteHttpUrl(normalizedBase)) {
    const baseUrl = new URL(normalizedBase);
    const basePath = baseUrl.pathname.endsWith('/')
      ? baseUrl.pathname.slice(0, -1)
      : baseUrl.pathname;
    baseUrl.protocol = socketProtocol;
    baseUrl.pathname = `${basePath}${normalizedPath}`;
    baseUrl.search = '';
    baseUrl.hash = '';
    return baseUrl.toString();
  }

  const originUrl = new URL(currentOrigin);
  originUrl.protocol = socketProtocol;
  originUrl.pathname = `${normalizedBase}${normalizedPath}`;
  originUrl.search = '';
  originUrl.hash = '';
  return originUrl.toString();
}

const API_BASE = resolveApiBase();

export function apiUrl(path: string): string {
  return buildApiUrl(API_BASE, path);
}

export function apiWebSocketUrl(path: string): string {
  if (typeof window === 'undefined') {
    return path;
  }

  return buildApiWebSocketUrl(API_BASE, path, window.location.origin, window.location.protocol);
}
